import { Clickable, Col, H2, H3, H4, List, P, PageNavigationBar, Row, TypeWrapper, Wrapper } from '@ahmdigital/ui';
import { ReactNode } from 'react';
import classNames from 'classnames';
import getOpeningHours from '@ahmdigital/logic/lib/utils/get-opening-hours';

import { CalloutBox, ContentContainer, LastPart, MiddlePart, SectionContainer } from './styles';
import BackToTop from '../../elements/back-to-top';
import Banner from '../../elements/banner';
import constants from '../../ahm-constants';
import Head from '../../../utils/head';
import LegacyBorderedWrapper from '../../components/legacy-bordered-wrapper';
import styles from './styles.module.sass';
import textStyles from '../../application/common/text.module.sass';

// Phone Links
const LINK_PHONE_AHM_DOMESTIC = (
  <Clickable to={`tel:${constants.CONTACT.PHONE.STANDARD}`}>{constants.CONTACT.PHONE.PRETTY}</Clickable>
);
const LINK_PHONE_AHM_INTERNATIONAL = (
  <Clickable to={`tel:${constants.CONTACT.PHONE.INTERNATIONAL_STANDARD}`}>
    {constants.CONTACT.PHONE.INTERNATIONAL_PRETTY}
  </Clickable>
);

// Email links
const ahmInfoEmail = constants.CONTACT.EMAIL.INFO;
const LINK_EMAIL_AHM_INFO = (
  <Clickable to={`mailto:${ahmInfoEmail}?subject=Enter your membership number`}>{ahmInfoEmail}</Clickable>
);

const medibankPrivacyTeamEmail = 'privacy@amplarhealth.com.au';
const LINK_EMAIL_MEDIBANK_PRIVACY_TEAM = (
  <Clickable to={`mailto:${medibankPrivacyTeamEmail}`}>{medibankPrivacyTeamEmail}</Clickable>
);

const medibankPrivacyOfficerEmail = 'privacy@medibank.com.au';
const LINK_EMAIL_MEDIBANK_PRIVACY_OFFICER = (
  <Clickable to={`mailto:${medibankPrivacyOfficerEmail}`}>{medibankPrivacyOfficerEmail}</Clickable>
);

// URL links
const LINK_URL_AHM_CAR_AND_HOME_INSURANCE_PRIVACY_POLICY = (
  <Clickable to={constants.URLS.SALES.CAR_AND_HOME_INSURANCE_PRIVACY_POLICY}>privacy policy</Clickable>
);
const LINK_URL_PRIVACY_GOV = (
  <Clickable to={constants.URLS.THIRD_PARTY.OFFICE_AUSTRALIAN_INFORMATION_COMMISSIONER}>oaic.gov.au</Clickable>
);
const LINK_URL_PRIVACY_HOLLARD = (
  <Clickable to={constants.URLS.THIRD_PARTY.HOLLARD_PRIVACY_POLICY}>Hollard’s privacy policy</Clickable>
);
const LINK_URL_PRIVACY_MEDIBANK = (
  <Clickable to={constants.URLS.THIRD_PARTY.MEDIBANK_PRIVACY}>{constants.URLS.THIRD_PARTY.MEDIBANK_PRIVACY}</Clickable>
);

type CommunicationSectionProps = {
  contentOpeningHours: string;
};

// Fragments
const CommunicationSection = ({ contentOpeningHours }: CommunicationSectionProps) => (
  <>
    <H4 looksLike="h5"> ahm:</H4>
    <List>
      <li>
        Access the Settings page within the ahm Online Member Services facility (note: this option is not available for
        ahm OSHC members); or
      </li>
      <li>
        Call us on {LINK_PHONE_AHM_DOMESTIC} or {LINK_PHONE_AHM_INTERNATIONAL} {contentOpeningHours} (except ahm OSHC
        members); or
      </li>
      <li>
        for ahm OSHC members, 134 148 (within Australia) or +61 3 9862 1095 (outside Australia), 8am – 8pm Monday to
        Friday (AEST); or
      </li>
      <li>Email us at {LINK_EMAIL_AHM_INFO}</li>
    </List>
    <H4 looksLike="h5">MHS:</H4>
    <List>
      <li>
        Please contact {LINK_EMAIL_MEDIBANK_PRIVACY_TEAM} and ensure that you advise the programme (or product or
        service) you are enrolled in or receiving and the MHS company providing it.
      </li>
    </List>
    <H4 looksLike="h5">Apps:</H4>
    <List>
      <li>
        Access the about me page in Medibank’s lifestyle and wellbeing apps (for in app preferences only – you will need
        to contact us by one of the other channels above for non-app related communications preferences)
      </li>
    </List>
  </>
);

type ContentSectionProps = {
  children: ReactNode;
};

const ContentSection = ({ children }: ContentSectionProps) => (
  <TypeWrapper variant={[styles.section, 'marginBottomSpacer7']}>{children}</TypeWrapper>
);

const PrivacyPolicyPage = () => {
  const contentOpeningHours = getOpeningHours(constants.AHM_SERVICE_NAME.SALES);

  return (
    <LegacyBorderedWrapper>
      <Head>
        <title>Privacy Policy</title>
        <meta
          name="description"
          content="Your privacy is our biggest priority. Read about how we keep you and your information safe."
        />
      </Head>
      <Banner
        title="Privacy policy"
        description="Your right to privacy is one of the most important things to us at ahm."
        note="Effective as of 13 January 2026"
      />
      {/* @ts-expect-error - Automatic, Please fix when editing this file */}
      <ContentContainer id="backToTopPoint" className={classNames([textStyles.text, styles.content])} tabIndex="-1">
        <PageNavigationBar
          items={[
            {
              // @ts-expect-error [🤖 PLEASE FIX 🤖]: Type '{ id: string; text: string; }' is not assignable to type '{ text: ReactNode; }'.
              id: 'introduction',
              text: 'Introduction',
            },
            {
              // @ts-expect-error [🤖 PLEASE FIX 🤖]: Type '{ id: string; text: string; }' is not assignable to type '{ text: ReactNode; }'.
              id: 'general-terms',
              text: 'General Terms',
            },
            {
              // @ts-expect-error [🤖 PLEASE FIX 🤖]: Type '{ id: string; text: string; }' is not assignable to type '{ text: ReactNode; }'.
              id: 'health-insurance-services',
              text: 'Health Insurance Services',
            },
            {
              // @ts-expect-error [🤖 PLEASE FIX 🤖]: Type '{ id: string; text: string; }' is not assignable to type '{ text: ReactNode; }'.
              id: 'all-other-services',
              text: 'All Other Services',
            },
          ]}
          variant="white"
        />
        <SectionContainer>
          <Wrapper>
            <Row>
              <Col md={12} df={8}>
                <H2 variant="paddingBottomSpacer10" looksLike="h3">
                  Contents
                </H2>
                <H3 looksLike="h4">Part 1: General terms</H3>
                <P>Find out about:</P>
                <List>
                  <li>Types of personal information we may collect</li>
                  <li>Why, when and how personal information is collected and held</li>
                  <li>Why we need your personally identifiable information</li>
                  <li>Use of your personal information</li>
                  <li>Disclosing your personal information</li>
                  <li>Communicating with you and direct marketing</li>
                  <li>How you can access your personal information</li>
                  <li>How you can correct personal information</li>
                  <li>Disclosure overseas</li>
                  <li>Privacy Policy Updates</li>
                  <li>How you can contact us</li>
                </List>
                <H3 looksLike="h4">Part 2: Health insurance services</H3>
                <P>Find out about:</P>
                <List>
                  <li>The types of personal information we may collect</li>
                  <li>When we collect personal information</li>
                  <li>Use of your personal information</li>
                  <li>Disclosing your personal information</li>
                  <li>Couples and family health insurance policies</li>
                  <li>Corporate health insurance policies for religious organisations</li>
                </List>
                <H3 looksLike="h4">Part 3: All other services</H3>
                <P>Find out about:</P>
                <List>
                  <li>The types of personal information we may collect</li>
                  <li>When we collect your personal information</li>
                  <li>Use of your personal information</li>
                  <li>How we disclose personal information</li>
                  <li>How we handle personal information as a contracted health service provider</li>
                </List>
              </Col>
            </Row>
            <MiddlePart id="introduction">
              <Row>
                <Col md={12} df={8}>
                  <H2 variant="paddingBottomSpacer10" looksLike="h3">
                    Introduction
                  </H2>
                  <H3 looksLike="h4">Who we are</H3>
                  <P>
                    We are Medibank Private Limited ABN 47 080 890 259 (Medibank) trading as ahm, and our subsidiaries
                    including:
                  </P>
                  <List>
                    <li>Australian Health Management Group Pty Ltd ABN 96 003 683 298 (ahm)</li>
                    <li> References to ‘us’, ‘we’ or ‘our’ include Medibank, trading as ahm.</li>
                  </List>
                  <P>
                    In this privacy policy you will see the terms ‘personal information’ and ‘sensitive information’
                    used. These terms have the following definitions:
                  </P>
                  <List>
                    <li>
                      ‘personal information’ means information that identifies you or can be used to identify you, or
                      from which you are reasonably identifiable.
                    </li>
                    <li>
                      ‘sensitive information’ is a sub-set of personal information and includes information about your
                      health, health services provided to you and your claims. Sensitive information is more protected
                      under Privacy Laws than are other forms of personal information.
                    </li>
                    <li>
                      In this privacy policy, and unless otherwise stated, all references to ‘personal information’
                      include ‘sensitive information’.
                    </li>
                  </List>
                  <H3 looksLike="h4">This privacy policy applies to:</H3>
                  <List>
                    <li>All current and past members of ahm whose personal information we have collected</li>
                    <li>
                      All individuals whose personal information is collected in relation to the products and services
                      offered by ahm
                    </li>
                    <li>
                      All individuals whose personal information is collected by us in the course of our functions and
                      activities, such as service providers, contractors and prospective employees.
                    </li>
                  </List>
                  <P>
                    For the privacy policy that applies to personal information collected in relation to the products
                    and services offered by the Medibank Group Companies outside of the ahm brand, please refer to{' '}
                    {LINK_URL_PRIVACY_MEDIBANK}.
                  </P>
                </Col>
              </Row>
            </MiddlePart>
            <MiddlePart id="general-terms">
              <Row>
                <Col md={12} df={8}>
                  <H2 variant="paddingBottomSpacer10" looksLike="h3">
                    Part 1: General terms
                  </H2>
                </Col>
              </Row>
              <ContentSection>
                <Row>
                  <Col md={12} df={8}>
                    <H3 looksLike="h4">Section 1: The types of personal information we may collect</H3>
                    <P>
                      The types of personal information we may collect depends on our relationship with you, and may
                      include:
                    </P>
                    <List>
                      <li>identifying information such as name, date of birth and employment details;</li>
                      <li>
                        identification information for identity verification, such as your driver’s licence and ahm
                        member card;
                      </li>
                      <li>
                        contact information such as home address, home and mobile phone numbers and email address, and
                        in some cases your work contact details;
                      </li>
                      <li>government-issued identifiers including Medicare numbers;</li>
                      <li>financial information, such as bank account and card details;</li>
                      <li>
                        sensitive information, including:
                        <List>
                          <li>information about your health, health services provided to you and your claims;</li>
                          <li>biometric information and templates, such as voice recognition information;</li>
                          <li>
                            lifestyle, diet, exercise and health related information that you self input into our
                            wellbeing apps;
                          </li>
                          <li>
                            other sensitive information – such as your race and ethnicity where we are required to
                            collect it;
                          </li>
                        </List>
                      </li>
                      <li>information about your activities, including sporting and other lifestyle interests;</li>
                      <li>
                        information about involvement in other programs you participate in or memberships you may have;
                      </li>
                      <li>your ahm membership number, if you sign up for other ahm products or services;</li>
                      <li>
                        your IP and/or IMEI information to detect unauthorised access to your membership and identify
                        potential fraud and criminal behaviour; and
                      </li>
                      <li>
                        by using cookies and similar technologies, information about your usage of our website, apps and
                        social media platforms for the purposes of analytics (including when you use our website and
                        apps and what you do, and the information you input, while using them), to provide a
                        personalised experience, support navigation, remember your preferences and, subject to your
                        marketing preferences – to target marketing to you (based upon your demographic information and
                        use of our app and website). You can limit our use of cookies using the privacy settings on your
                        browser (by choosing not to accept cookies), but this will not apply to other technologies we
                        use to track your browsing activity. Please note, turning off all cookies may affect your
                        browsing experience.
                      </li>
                    </List>
                  </Col>
                  <Col md={12} df={4}>
                    <CalloutBox>
                      The personal information we collect depends on your relationship with us. It can be information we
                      use to identify and contact you, and manage your account (if any). Some of the information can be
                      sensitive, like details about your health.
                    </CalloutBox>
                  </Col>
                </Row>
              </ContentSection>
              <ContentSection>
                <Row>
                  <Col md={12} df={8}>
                    <H3 looksLike="h4">Section 2: Why, when and how personal information is collected and held</H3>
                    <H4 looksLike="h5">Why we collect and use your personal information</H4>
                    <P>
                      We collect your personal information to enable Medibank Group Companies and our third party
                      suppliers and partners to provide you with products and services, including insurance,
                      health-related services, partner offerings and information on other products and services
                      (collectively Insurance and Health Products). We may also be required by law to collect some
                      personal information.
                    </P>
                    <P>
                      Where you provide personal information to the Medibank Group Companies as a service provider,
                      contractor or prospective employee, we collect your personal information to enable us to fulfil
                      the purpose and related purposes for which you provided the information.
                    </P>
                    <H4 looksLike="h5">How we collect your personal information</H4>
                    <P>We will only collect personal information about you by lawful and fair means.</P>
                    <P>We may collect personal information from you at various times, including:</P>
                    <List>
                      <li>
                        when you open and start to complete – or complete – an application form or other type of form in
                        relation to our products and services;
                      </li>
                      <li>when you contact us in person, by phone, mail, email or online;</li>
                      <li>when you make a claim;</li>
                      <li>when you visit premises from which we operate; and</li>
                      <li>
                        when you visit our website or subscribe to or use one of our lifestyle and wellbeing apps.
                      </li>
                    </List>
                    <P>
                      We may collect your personal information from you, from another person covered by your policy,
                      from a person authorised to provide us with your personal information on your behalf, or from an
                      agency or organisation on whose behalf we are providing you with services or products (as agent
                      for a principal).
                    </P>
                    <P>We may also collect information about you from other sources, such as:</P>
                    <List>
                      <li>
                        a third party such as a hospital, dentist or optometrist or other health service provider who
                        has treated you;
                      </li>
                      <li>
                        an employer, educational institution, government agency or adviser who has dealt with you (or
                        their authorised representatives);
                      </li>
                      <li>for overseas customers, your migration or other agent;</li>
                      <li>
                        Medibank Group Companies who have provided you with services including health-related services;
                      </li>
                      <li>
                        a service provider engaged by us – or a third party who partners with us – to assist us in
                        providing goods or services or administering our business (such as mailhouses, printing, and IT
                        service providers and platforms, or marketing, planning and product or service development)
                      </li>
                      <li>if you are a health service provider, from relevant databases and directories;</li>
                      <li>
                        publicly available sources (for example, media, social media, and public reviews). We may do
                        this for the purposes of understanding our customers’ experience, recruitment, preventing fraud,
                        confirming identity, and other business purposes;
                      </li>
                      <li>CCTV cameras in operation at our offices and retail centres;</li>
                      <li>
                        the main policy holder (or person setting up a policy to cover you) at the time that they incept
                        (set up) the policy; and
                      </li>
                      <li>
                        another health fund and your co-insured, if you have requested a transfer of your health
                        insurance between that fund and us, so as to facilitate that transfer.
                      </li>
                    </List>
                    <P>We also obtain information from other sources where:</P>
                    <List>
                      <li>
                        we provide products and services on behalf of or in conjunction with others, including business
                        partners
                      </li>
                      <li>
                        we need information from third parties relating to a product or service we provide to you, or
                        relating to a health insurance claim
                      </li>
                      <li>we need information to prevent or minimise the risk of fraud</li>
                      <li>
                        you have consented to third parties sharing it with us, such as people you have authorised to
                        deal with your policy.
                      </li>
                    </List>
                    <P>
                      Where we engage with you multiple times over a short period in relation to the same matter, we may
                      not provide you with a separate notice about privacy each time we engage with you.
                    </P>
                    <H4 looksLike="h5">How we hold your personal information</H4>
                    <P>
                      We take reasonable steps to protect your personal information from misuse and loss and from
                      unauthorised access, modification or disclosure.
                    </P>
                    <P>
                      We aim to store your information securely and have a range of security controls in place
                      (including physical, technical and procedural safeguards) designed to protect your personal
                      information.
                    </P>
                    <P>Our employees and contractors regularly receive targeted privacy training.</P>
                    <P>
                      We take reasonable steps to make sure that the personal information about you – that we collect,
                      use and disclose – is accurate, complete, up to date and relevant.
                    </P>
                    <H4 looksLike="h5">When and how we dispose of your personal information</H4>
                    <P>
                      We seek to keep your personal information for only as long as it is required in order to provide
                      you with products and services or to legitimately comply with our business and legal obligations
                      and requirements. When it is no longer needed for these purposes, we may destroy or permanently
                      de-identify this personal information. Consequently, if you request access to your old personal
                      information, we may not be able to provide you with your records where they have been destroyed or
                      de-identified.
                    </P>
                    <H4 looksLike="h5">De-identifying your information</H4>
                    <P>
                      Where both possible and in our view – appropriate, where using your personal information, we will
                      seek to de-identify it, so that your identity is not readily ascertainable from the de-identified
                      information or from triangulating your de-identified information with other sources of
                      information.
                    </P>
                  </Col>
                  <Col md={12} df={4}>
                    <CalloutBox>
                      We collect personal information so that we can provide products and services to our customers and
                      members. We also collect it when required by law and for any other purpose which you have provided
                      us with the information. We do this at different times and in several ways.
                    </CalloutBox>
                  </Col>
                </Row>
              </ContentSection>
              <ContentSection>
                <Row>
                  <Col md={12} df={8}>
                    <H3 looksLike="h4">
                      Section 3: Identifying yourself, and why we need your personally identifiable information
                    </H3>
                    <P>
                      You generally have the right not to identify yourself when dealing with us and to use a pseudonym,
                      where it is lawful and practicable for us to allow it. However, in many instances we will need
                      your identity details. For example, we will need your name and date of birth, if you want to have
                      private health insurance coverage with us that receives the applicable government rebate.
                    </P>
                    <P>
                      If you do not provide or authorise the provision of personal information we request, we may be
                      unable to provide you with some or all of our products and services or the products and services
                      of our partners. If you ask us, we will tell you what personal information we must have in order
                      to provide you with a particular product or service, and what requested personal information is
                      optional for that product or service.
                    </P>
                    <P>
                      Subject to the applicable Privacy Laws, by becoming or remaining a member of one of our policies
                      or by otherwise providing personal information to us, you confirm that you have consented to us
                      collecting, using and disclosing your personal information, however collected by us, in accordance
                      with this privacy policy (as amended and notified to you from time to time).
                    </P>
                  </Col>
                  <Col md={12} df={4}>
                    <CalloutBox>
                      In some limited circumstances, you may be able to choose whether to identify yourself when you
                      deal with us.
                    </CalloutBox>
                  </Col>
                </Row>
              </ContentSection>
              <ContentSection>
                <Row>
                  <Col md={12} df={8}>
                    <H3 looksLike="h4">Section 4: Using your personal information</H3>
                    <P>We may use your personal information for these purposes, including to:</P>
                    <List>
                      <li>process your policy application and manage your policy;</li>
                      <li>manage our relationship with you;</li>
                      <li>identify and communicate with you;</li>
                      <li>provide you with requested information, products or services;</li>
                      <li>process and audit payments and claims;</li>
                      <li>analyse, investigate, pursue and prevent suspected fraudulent activities;</li>
                      <li>manage and develop Insurance and Health Products;</li>
                      <li>
                        assess your suitability for and contact you about Insurance and Health Products that we believe
                        may be of benefit to you;
                      </li>
                      <li>partner or work with third parties to improve our membership offering and value;</li>
                      <li>manage and develop our business and operational processes and systems;</li>
                      <li>
                        where permitted under law, conduct marketing – including targeted electronic marketing (such as
                        emails, or advertisements on websites and social media platforms that you access);
                      </li>
                      <li>
                        obtain feedback, and engage in analytic and research activities (inclusive of a wide range of
                        analytics and customer behavioural research projects);
                      </li>
                      <li>manage and resolve any legal, clinical or commercial complaints or issues;</li>
                      <li>perform other functions and activities relating to our business;</li>
                      <li>
                        in the case of your voice recording, use your voice to help us improve our voice-imprinting IVR
                        (as an antifraud and detection) and refine our voice recognition software;
                      </li>
                      <li>comply with our legal obligations or enforce our legal rights; and</li>
                      <li>as otherwise required or authorised by law, including the Privacy Laws.</li>
                    </List>
                    <P>
                      We will use your personal information, including call recordings for training, coaching and
                      development purposes unless you ask us not to.
                    </P>
                  </Col>
                  <Col md={12} df={4}>
                    <CalloutBox>
                      We use your personal information in a number of ways. This includes what we need to do to manage
                      our relationship with you, and what’s required by law.
                    </CalloutBox>
                  </Col>
                </Row>
              </ContentSection>
              <ContentSection>
                <Row>
                  <Col md={12} df={8}>
                    <H3 looksLike="h4">Section 5: Disclosing and sharing your personal information</H3>
                    <P>
                      In pursuing the purposes for which we may collect and use your personal information, we may
                      disclose your personal information to persons or organisations in Australia and overseas
                      including:
                    </P>
                    <List>
                      <li>
                        Medibank Group Companies for the purpose of:
                        <List>
                          <li>
                            assisting you to ascertain if your existing cover is adequate for your current and
                            foreseeable future needs;
                          </li>
                          <li>
                            offering ancillary services, health management programs and services which may be of benefit
                            to you (for example, if you advise us that you will be having an operation, we may supply,
                            and you consent to us supplying, information to a Medibank Group Company so they can offer
                            rehabilitation and nursing services to assist you in your recovery and promote a positive
                            outcome from your surgery);
                          </li>
                          <li>
                            ensuring that our customer records for you between ahm and Medibank are consistent, and
                            accurate;
                          </li>
                        </List>
                      </li>
                      <li>our agents and service providers;</li>
                      <li>our professional advisors;</li>
                      <li>health service providers;</li>
                      <li>
                        other persons covered by your policy as part of administering the policy and paying benefits;
                      </li>
                      <li>
                        potential or actual buyers of our assets or business, including only some assets or parts of our
                        business;
                      </li>
                      <li>payment system operators and financial institutions;</li>
                      <li>your agents and advisors or other persons authorised by, or responsible for, you;</li>
                      <li>government agencies;</li>
                      <li>
                        your educational institution, migration agent or broker if you have overseas student health
                        cover (OSHC) or a visitors cover product;
                      </li>
                      <li>
                        third party insurers whom we are authorised to represent if you purchase other insurance
                        products through us;
                      </li>
                      <li>
                        third parties and other members of the Medibank Group with whom Medibank partners or works with
                        – to improve your opportunities or improve your wellbeing and/or the value you get from your
                        membership;
                      </li>
                      <li>
                        other health funds, service providers or other third parties who assist us in the detection and
                        investigation of fraud;
                      </li>
                      <li>
                        in relation to a transfer certificate requested by you or the main policy holder – your
                        co-insured (please note that a transfer certificate will show if anyone covered by the policy
                        has exercised their lifetime mental health waiver, as we are required by law to include this
                        detail);
                      </li>
                      <li>
                        your employer (or their authorised representatives) if you have a corporate insurance product;
                        and
                      </li>
                      <li>other parties to whom we are authorised or required by law to disclose information.</li>
                    </List>
                    <H4 looksLike="h5">
                      The collection, use and disclosure of your information should you subscribe to one of our
                      lifestyle and wellbeing apps
                    </H4>
                    <P>
                      Where you have subscribed to one of our health and well-being applications (app) and consented by
                      agreeing to the terms and conditions of the app, then in addition to the general purposes of use
                      and disclosure of your information set out in this privacy policy:
                    </P>
                    <List>
                      <li>
                        we will collect, use and disclose your self-inputted personal, biometric, health, exercise and
                        diet information and automatically inputted information from connected apps and devices for the
                        purposes of that app in helping you to achieve your wellbeing goals;
                      </li>
                      <li>
                        to facilitate the operation of the app, the app provider will also handle your information on
                        our behalf and necessary information about you will be provided to rewards partners to allow you
                        to redeem your rewards;
                      </li>
                      <li>
                        you can choose how much information you disclose to us, and separately to other app users and
                        your competition or team members, within the privacy settings and social posting functions of
                        the app – at any time, but you must provide some personal information such as your email
                        address, phone type, browser type, operating system and IP address to be able to use the app;
                      </li>
                      <li>
                        you can choose to use a pseudonym in using the app (but you may not be able to claim full
                        rewards with a pseudonym);
                      </li>
                    </List>
                    <P>If you link any of our lifestyle apps to your wearable fitness devices:</P>
                    <List>
                      <li>you may be asked to agree to the app provider’s privacy policy;</li>
                      <li>
                        we are not responsible for the collection and use practices of the app provider in these
                        circumstances, in respect of your personal information;
                      </li>
                      <li>
                        please visit the app provider’s website to understand their privacy practices and options they
                        may make available to you in relation to their collection and use of your personal information.
                      </li>
                    </List>
                  </Col>
                  <Col md={12} df={4}>
                    <CalloutBox>
                      There’s a range of people and organisations we may share your personal information with. We share
                      personal information only as legally permitted.
                    </CalloutBox>
                  </Col>
                </Row>
              </ContentSection>
              <ContentSection>
                <Row>
                  <Col md={12} df={8}>
                    <H3 looksLike="h4">Section 6: Protecting your personal information</H3>
                    <H4 looksLike="h5">If your personal information is hacked or inadvertently disclosed</H4>
                    <P>
                      If we become aware that we have inappropriately used or disclosed your personal information, or
                      that the security of your personal information has been compromised (a data breach), and we are
                      unable to rectify the data breach without any potential adverse effect on your privacy, we may
                      contact you to inform you, and to work with you to minimise or mitigate the consequences of the
                      data breach. Pursuant to the Notifiable Data Breaches scheme (under Part IIIC of the Privacy Act
                      1988), we may be required to notify you of a data breach as soon as we practicably can if we
                      consider you are reasonably likely to be at risk of serious harm (including financially or to your
                      mental or physical wellbeing). Where reasonably practicable we will give you details of the data
                      breach and, where possible, steps you could take to lower the risk of harm to you. We may make a
                      public notification for a data breach affecting a large number of customers, before we contact you
                      directly or in place of direct contact.
                    </P>
                    <H4 looksLike="h5">Protect yourself and your PIN</H4>
                    <P>
                      If you have a private health insurance policy with us, we encourage you to add a unique personal
                      identification number (PIN) to your membership – to give you additional privacy protection.
                    </P>
                    <P>Please protect your PIN by:</P>
                    <List>
                      <li>keeping your PIN confidential,</li>
                      <li>
                        not writing it down or keeping a record of it with your membership card or policy documents,
                      </li>
                      <li>not choosing an easy to guess PIN (such as your date of birth), and</li>
                      <li>
                        not disclosing your PIN to other people covered by your policy or authorised to transact on your
                        policy (as they need to have their own PINs).
                      </li>
                    </List>
                    <P>
                      If you think that your PIN has been compromised please let us know and change your PIN as soon as
                      possible.
                    </P>
                  </Col>
                  <Col md={12} df={4}>
                    <CalloutBox>
                      If we become aware that we have inappropriately used or disclosed your personal information, or
                      that the security of your personal information has been compromised (a data breach), and we are
                      unable to rectify the data breach without any potential adverse effect on your privacy, we may
                      contact you to inform you, and to work with you to minimise or mitigate the consequences of the
                      data breach.
                    </CalloutBox>
                  </Col>
                </Row>
              </ContentSection>
              <ContentSection>
                <Row>
                  <Col md={12} df={8}>
                    <H3 looksLike="h4">Section 7: Communicating with you and direct marketing</H3>
                    <H4 looksLike="h5">How we communicate with you</H4>
                    <P>
                      To keep you informed quicker, where you provide us with an email address, we send most
                      service-related communications to you by email. Service-related communications are the essential
                      things you need to know about your cover, like annual tax statements, changes to premiums and
                      account notices. In some circumstances and for some products, including some ahm products, we
                      require an email address to communicate with you as a term and condition of the product. You can
                      otherwise choose how we communicate with you by contacting us as follows:
                    </P>
                    <CommunicationSection contentOpeningHours={contentOpeningHours} />
                    <H4 looksLike="h5">Direct marketing</H4>
                    <P>
                      From time to time, where permitted under law, we may collect and use your personal information so
                      that we can promote and market Insurance and Health Products to you and keep you informed of
                      special offers from Medibank Group Companies and third parties. We may contact you in relation to
                      these promotions and offers by direct mail, SMS and MMS messages, targeted marketing on social
                      media platforms, in app and push notification, by phone and email.
                    </P>
                    <P>
                      You can opt out of marketing by contacting us. However, if you opt out of marketing you will still
                      receive service related communications from us. If you opt out of marketing, please be aware that
                      your details may still be shared with our marketing partners and/or mailhouses for the purposes of
                      ensuring that they do not market to you. For example, we may partner with a provider to market our
                      products to their customers using their customer lists, but will provide a list of our ‘opted out’
                      customers to wash against their customer list securely, so as to ensure that customers who have
                      opted out of our marketing, do not receive marketing. If you have signed up to receive marketing
                      from us or from our marketing partners and/or mailhouses via different email addresses, you may
                      still receive marketing at any email addresses for which you have not opted out, as the above
                      process will only identify where opted out email addresses are identical. Therefore, please tell
                      us all email addresses you wish to opt out of receiving marketing to - in order to stop receiving
                      marketing from us or on our behalf to those email addresses.
                    </P>
                    <P>
                      If you want to change your communications preferences, please let us know. Please be aware that if
                      you are an ahm customer, we require you to have an email address registered with us for service
                      communications.
                    </P>
                    <P>
                      To opt out of marketing or change your communications preferences please contact us as set out
                      below. Please note that if you opt out of marketing from Medibank Group Companies, this will not
                      opt you out of in-app marketing in or related to our lifestyle and wellbeing apps, and vice versa.
                      If you wish to opt out of marketing in our lifestyle and wellbeing apps you will need to do so in
                      the privacy settings of the relevant app (set out below).
                    </P>
                    <CommunicationSection contentOpeningHours={contentOpeningHours} />
                  </Col>
                  <Col md={12} df={4}>
                    <CalloutBox>
                      We send both service-related and, from time to time, marketing communications; you can decide
                      whether to receive marketing communications or not.
                    </CalloutBox>
                  </Col>
                </Row>
              </ContentSection>
              <ContentSection>
                <Row>
                  <Col md={12} df={8}>
                    <H3 looksLike="h4">Section 8: How you can access your personal information </H3>
                    <P>
                      You can ask us for access to the information we hold about you at any time. We will endeavour to
                      respond in a reasonable time, being within 30 days and as soon as is reasonably possible.
                    </P>
                    <P>
                      We will generally not charge a fee for accessing your personal information. We will only charge a
                      fee to access information in exceptional circumstances, and where your request is particularly
                      onerous. We will let you know in advance of levying any fee to confirm that you still wish to
                      proceed with your request.
                    </P>
                    <P>
                      When you contact us to seek access to your personal information, we will need to be reasonably
                      satisfied it is you, and not an unauthorised person.
                    </P>
                    <P>
                      We may require you to substantiate your identity to protect you from fraud and privacy breaches
                      perpetrated by third parties pretending to be you.
                    </P>
                    <P>
                      We may also use a third party and secure service to confirm your identity (such as two factor
                      authentication), and your IMEI or IP address in some circumstances, to reduce the risk to you of
                      identity theft and reduce fraud risk to us.
                    </P>
                    <P>We may not always give you access to certain information you have requested, such as where:</P>
                    <List>
                      <li>we no longer hold or use the information and have destroyed or de-identified it</li>
                      <li>providing access would be unlawful</li>
                      <li>we are required or authorised by law to deny access</li>
                      <li>providing access would unreasonably impact on the privacy of others</li>
                      <li>
                        we cannot be satisfied that you are who you say you are (we cannot adequately identify you).
                      </li>
                    </List>
                    <P>
                      It would assist us to ensure we properly understand your request, and allow us to respond more
                      promptly, if requests are made in writing and include as much detail as possible.
                    </P>
                  </Col>
                  <Col md={12} df={4}>
                    <CalloutBox>
                      You can ask us for any personal information we hold about you anytime – you will need to verify
                      your identity and identify which records you wish to obtain, and give us a reasonable time to get
                      it back to you.
                    </CalloutBox>
                  </Col>
                </Row>
              </ContentSection>
              <ContentSection>
                <Row>
                  <Col md={12} df={8}>
                    <H3 looksLike="h4">Section 9: Correcting your personal information</H3>
                    <P>
                      To enable us to provide the best services to you, it is important the information we hold about
                      you is up to date. Please contact us when your details change.
                    </P>
                    <P>
                      If you believe any information we hold about you is inaccurate, incomplete or out of date, please
                      let us know. We will take reasonable steps to amend any personal information about you which is
                      inaccurate or out of date.
                    </P>
                    <P>
                      You can get in touch with us at Medibank and ahm to request the above any time you wish to do so.
                    </P>
                    <P>
                      In some circumstances, we may refuse to correct your personal information. Where this happens, we
                      will provide you with reasons for this decision (except to the extent that it would be
                      unreasonable to do so), seek alternatives and take any further legally required steps.
                    </P>
                  </Col>
                  <Col md={12} df={4}>
                    <CalloutBox>
                      It’s important to make sure that the details we have for you are correct. You can correct them at
                      any time; just contact us and follow our instructions.
                    </CalloutBox>
                  </Col>
                </Row>
              </ContentSection>
              <ContentSection>
                <Row>
                  <Col md={12} df={8}>
                    <H3 looksLike="h4">Section 10: Disclosure overseas</H3>
                    <P>
                      We may disclose your personal information to recipients located outside of Australia as part of
                      our ordinary business, functions and activities. These overseas recipients include our service
                      providers or their related entities and their sub-contractors, which provide support and
                      assistance to us in delivering our products and services to you. They are likely to be located in
                      the following countries:
                    </P>
                    <List>
                      <li>Canada</li>
                      <li>European Union</li>
                      <li>Fiji</li>
                      <li>India</li>
                      <li>Indonesia</li>
                      <li>Japan</li>
                      <li>Mauritius</li>
                      <li>New Zealand</li>
                      <li>Philippines</li>
                      <li>Singapore</li>
                      <li>South Africa</li>
                      <li>United Kingdom</li>
                      <li>United States</li>
                      <li>Vietnam</li>
                    </List>
                    <P>
                      This list is updated from time to time. You can visit our website at any time to view the latest
                      version (listed in the most up to date version of this Privacy Policy).
                    </P>
                    <P>
                      On occasion, we may also disclose your personal information to overseas organisations where you
                      instruct us or expressly consent to us doing so. In such cases, we may not be able to ensure
                      adequate protection in relation to those organisations’ management of your information.
                    </P>
                    <P>
                      We may also disclose your personal information to an overseas recipient if you have a corporate
                      health insurance product and we are instructed by your employer to disclose your information to
                      that overseas recipient in order to administer your policy.
                    </P>
                    <P>
                      If you have an OSHC or visitors cover product, we may also disclose your information to your
                      educational institution, migration agent or broker, in the country they are located.
                    </P>
                  </Col>
                  <Col md={12} df={4}>
                    <CalloutBox>
                      We may sometimes share your personal information with overseas recipients that support us to
                      deliver our products and services to you.
                    </CalloutBox>
                  </Col>
                </Row>
              </ContentSection>
              <ContentSection>
                <Row>
                  <Col md={12} df={8}>
                    <H3 looksLike="h4">Section 11: Privacy policy updates</H3>
                    <P>
                      We will review and update this privacy policy from time to time where appropriate to reflect
                      changes in applicable laws or how we handle your personal information. Where we make a significant
                      change to our privacy policy, we will notify you for example, via our website or apps.
                    </P>
                    <P>You can always find the most up-to-date version of our privacy policy on our website.</P>
                  </Col>
                  <Col md={12} df={4}>
                    <CalloutBox>We’ll notify you where we make a significant change to our privacy policy.</CalloutBox>
                  </Col>
                </Row>
              </ContentSection>
              <Row>
                <Col md={12} df={8}>
                  <H3 looksLike="h4">Section 12: How you can contact us</H3>
                  <P>
                    If you have any concerns or queries about the manner in which your personal information has been
                    handled, please contact our Privacy Officer whose contact details are provided below.
                  </P>
                  <P>
                    If you wish to make a formal complaint, please provide your complaint in writing to our Privacy
                    Officer, and detail information relevant to your complaint. Please note that we will receive and
                    action your request faster if you email it to us using the details below.
                  </P>
                  <P>
                    Medibank and ahm: Group Privacy Officer, Medibank Private Limited, post - GPO Box 9999 (Your Capital
                    City) or e-mail - {LINK_EMAIL_MEDIBANK_PRIVACY_OFFICER}
                  </P>
                  <P>We will consider your complaint promptly and contact you to seek to resolve the matter.</P>
                  <P>
                    Generally, we will contact you to acknowledge receipt of your complaint and let you know who is
                    managing your query within 5 business days of receiving it. We will attend promptly to your
                    complaint and will aim to respond to your concerns or otherwise keep you informed of our progress
                    within 30 days.
                  </P>
                  <P>
                    If we have not responded to you within a reasonable time or if your complaint is not resolved to
                    your satisfaction, you are entitled under the Privacy Act to make a complaint to the Office of the
                    Australian Information Commissioner and can find more information on the Commission’s website{' '}
                    {LINK_URL_PRIVACY_GOV}.
                  </P>
                </Col>
                <Col md={12} df={4}>
                  <CalloutBox>
                    <Clickable to={constants.URLS.SALES.CONTACT_US}>How to contact us.</Clickable>
                  </CalloutBox>
                </Col>
              </Row>
            </MiddlePart>
            <MiddlePart id="health-insurance-services">
              <Row>
                <Col md={12} df={8}>
                  <H2 variant="paddingBottomSpacer10" looksLike="h3">
                    Part 2: Health insurance services
                  </H2>
                  <P>
                    This Part applies to everyone who has, or has applied for, private health insurance with ahm. It
                    applies in addition to Part One.
                  </P>
                </Col>
              </Row>
              <ContentSection>
                <Row>
                  <Col md={12} df={8}>
                    <H3 looksLike="h4" variant="marginTopSpacer9">
                      Section 13: Couples and family health insurance policies
                    </H3>
                    <H4 looksLike="h5">Collecting your personal information</H4>
                    <P>
                      If you have a couples or family health insurance policy with us, we will collect information about
                      dependants (partner and children) from the policy holder who sets up the policy, with the policy
                      holder’s consent.
                    </P>
                    <P>
                      You may not realise it but your kids have a legal right to privacy once they turn 16. That’s why
                      ahm needs the contact details of your children once they turn 16, so we can make sure they’re OK
                      with you still viewing their claims history. If your child doesn’t consent, they you’ll no longer
                      be able to see their claims history. You can find out more in the{' '}
                      <Clickable to={constants.URLS.FILES.MEMBER_GUIDE}>Member Guide</Clickable> or by{' '}
                      <Clickable to={constants.URLS.SALES.CONTACT_US}>getting in touch with us</Clickable>.
                    </P>
                    <P>
                      If you are a policy holder and provide us with information about your partner or a dependant who
                      is 16 years or over, you need to:
                    </P>
                    <List>
                      <li>get their consent to give us their information;</li>
                      <li>tell them you have given us their personal information;</li>
                      <li>
                        tell them that our privacy practices are set out in this privacy policy and how they can access
                        this privacy policy; and
                      </li>
                      <li>tell them they are entitled to access their information by contacting us.</li>
                    </List>
                    <P>
                      If you are a policy holder and provide us with information about your partner or a dependant who
                      is 16 years or over, by providing that information you acknowledge that you are creating or that
                      you have created the policy on behalf of your co-insureds, and you warrant that:
                    </P>
                    <List>
                      <li>
                        you have their authority to agree to the relevant terms including consenting to the uses set out
                        in this privacy policy on their behalf;
                      </li>
                      <li>
                        you have made them aware of the information set out in this privacy policy and informed them of
                        how they can obtain access to this privacy policy; and
                      </li>
                      <li>
                        you have their consent to provide the information to us - and for us to use that information for
                        the purposes set out in this privacy policy - and as otherwise permitted by law, including the
                        Privacy Laws.
                      </li>
                    </List>
                    <P>
                      If a policy holder lodges a claim on your behalf, we act in reliance on the above warranties given
                      by the policy holder, and accordingly assume you have given your consent to the policy holder to
                      provide all the information we need to process your claim.
                    </P>
                    <H4 looksLike="h5">Using and disclosing your personal information</H4>
                    <P>
                      If you are a policy holder’s partner or dependant 16 years or older, we will not disclose
                      information about your health insurance claims (except claims payments) without your consent
                      unless required by law to do so (such as on a transfer certificate if you move to another health
                      insurer).
                    </P>
                    <P>All claims payments and general policy information will be sent to the policy holder.</P>
                    <P>The policy holder can:</P>
                    <List>
                      <li>change details on the policy</li>
                      <li>change level of cover</li>
                      <li>add and remove persons from the policy</li>
                      <li>receive benefits on behalf of dependants</li>
                      <li>terminate the policy.</li>
                    </List>
                    <P>
                      The policy holder can authorise their partner or dependant (16 years of age or over), to operate
                      the policy. If the policy holder gives such authority, the authorised person will have the same
                      level of access as the policy holder and so will be able to receive and view all personal
                      information in connection with the policy that the policy holder can see, including in respect of
                      claims made by the policy holder and co-insureds (where that information was available to the
                      policy holder). However (with the exception of Overseas Visitors Cover products) the authorised
                      person cannot:
                    </P>
                    <List>
                      <li>terminate the policy</li>
                      <li>remove the policy holder from the policy</li>
                    </List>
                    <P>
                      The policy holder may grant this authority. The authority will remain in place until the policy
                      holder contacts us to revoke it.
                    </P>
                    <H4 looksLike="h5">Your kids and privacy:</H4>
                    <P>
                      You may not realise, but your kids have a legal right to privacy once they turn 16. That’s why ahm
                      needs the contact details for your children once they turn 16, so we can make sure they’re okay
                      with you viewing their extras claims history. If your child doesn’t consent, then you’ll no longer
                      be able to see their extras claims history. You can find out more in the{' '}
                      <Clickable to={constants.URLS.FILES.MEMBER_GUIDE}>Member Guide</Clickable> or by{' '}
                      <Clickable to={constants.URLS.SALES.CONTACT_US}>getting in touch with us</Clickable>.
                    </P>
                    <H4 looksLike="h5">Relationship breakdowns</H4>
                    <P>
                      If the policy holder and their partner become divorced or separated, we require that the partner
                      be removed from the policy and take out a separate policy under our fund rules, and to prevent
                      privacy breaches. Please inform us promptly if this occurs so that we can take steps to enforce
                      these processes.
                    </P>
                    <P>
                      If your child is insured or not-insured under the policy of your ex-partner, we cannot confirm
                      this with you, or provide details about your ex-partner’s policy to you.
                    </P>
                    <P>
                      You may opt to pay for another person’s policy, but absent them giving you authority, this does
                      not permit us to otherwise disclose information about the policy to you. You can however, contact
                      us to cease your payments, but need to be aware that if you do this, we will contact the policy
                      holder to advise them that their policy will be or is un-financial due to a cancelled payment or
                      failed debit.
                    </P>
                  </Col>
                  <Col md={12} df={4}>
                    <CalloutBox>
                      <P>
                        If you have a couples or family health insurance policy with us, we will collect information
                        about dependants (partner and children) from the policy holder who sets up the policy, with the
                        policy holder’s consent.
                      </P>
                      <P>
                        You may not realise it but your kids have a legal right to privacy once they turn 16. That’s why
                        ahm needs the contact details of your children once they turn 16, so we can make sure they’re OK
                        with you still viewing their claims history.
                      </P>
                    </CalloutBox>
                  </Col>
                </Row>
              </ContentSection>
              <ContentSection>
                <Row>
                  <Col md={12} df={8}>
                    <H3 looksLike="h4">Section 14: Domestic and family violence</H3>
                    <P>
                      We may be able to offer additional privacy protections if you are a family violence victim or
                      identity theft victim. If you are a victim of family violence, stalking or identity theft, or
                      believe your identity may have been compromised and/or have personal safety concerns, we encourage
                      you to let us know to discuss further privacy protections that we may be able to provide you.
                    </P>
                  </Col>
                </Row>
              </ContentSection>
              <Row>
                <Col md={12} df={8}>
                  <H3 looksLike="h4">Section 15: Corporate health insurance policies for religious organisations</H3>
                  <P>
                    If you are a member of a religious order and covered by a corporate health insurance policy for your
                    religious order, pursuant to the authority you have provided, we will share your personal
                    information with the person responsible for administering that policy for your religious order, for
                    the purposes of processing your policy application and managing your policy. This includes:
                  </P>
                  <List>
                    <li>
                      Sharing information about:
                      <List>
                        <li>premiums (including any Australian government rebate which may apply)</li>
                        <li>
                          personal information such as name, address, date of birth of all members and dependants under
                          the policy
                        </li>
                        <li>details of your insurance cover</li>
                        <li>details about your claims</li>
                      </List>
                    </li>
                    <li>
                      Allowing your religious order’s administrative person to make changes to your health insurance
                      policy, including:
                      <List>
                        <li>Suspending your policy</li>
                        <li>Closing your policy</li>
                        <li>Changing the scale of your policy</li>
                        <li>Changing the cover of your policy</li>
                      </List>
                    </li>
                    <li>Administrative changes, such as ordering new cards</li>
                    <li>Directing where claims benefits are to be paid.</li>
                  </List>
                </Col>
                <Col md={12} df={4}>
                  <CalloutBox>
                    We share your personal information with the policy administrator. This is so that we can process
                    your application and manage your policy.
                  </CalloutBox>
                </Col>
              </Row>
            </MiddlePart>
            <LastPart id="all-other-services">
              <Row>
                <Col md={12} df={8}>
                  <H2 variant="paddingBottomSpacer10" looksLike="h3">
                    Part 3: All other services
                  </H2>
                  <P>
                    This Part applies to everyone who obtains services or products from Medibank Group Companies that
                    are not covered by Part Two. That includes car insurance, home and contents insurance and travel
                    insurance. This Part applies in addition to Part One.
                  </P>
                </Col>
              </Row>
              <ContentSection>
                <Row>
                  <Col md={12} df={8}>
                    <H3 looksLike="h4" variant="marginTopSpacer9">
                      Section 16: Access to limited personal information about you, when you obtain general insurance
                      products through us
                    </H3>
                    <P>
                      Medibank is an Authorised Representative for some general insurance companies. Where you seek a
                      general insurance product from us as an Authorised representative, we will disclose your
                      application information to the relevant general insurer. However we will not disclose information
                      about your private health insurance policy (except the fact that you or a co-policy holder are a
                      member of Medibank) and claims history to the general insurer, nor in acting as an Authorised
                      Representative will we refer to your other personal information held by us such as your membership
                      or claims information.
                    </P>
                  </Col>
                </Row>
              </ContentSection>
              <ContentSection>
                <Row>
                  <Col md={12} df={8}>
                    <H3 looksLike="h4">Section 17: Car insurance, Home and Contents insurance</H3>
                    <P>
                      Medibank is the promoter of ahm car and ahm home & contents insurance. Medibank Private Limited
                      ABN 47 080 890 259 (MPL) is an Authorised Representative, AR 000286089, of Open Insurance Pty Ltd
                      ABN 23 166 949 444 AFSL 451712 (Open). Open issues the insurance on behalf of the insurer. The
                      insurer is The Hollard Insurance Company Pty Ltd ABN 78 090 584 473, AFSL No. 241436 (Hollard).
                    </P>
                    <P>
                      For ahm car insurance, Open collects your personal information in accordance with its{' '}
                      {LINK_URL_AHM_CAR_AND_HOME_INSURANCE_PRIVACY_POLICY}. Open will ask for your name, contact details
                      (address, email and phone number), ahm membership number, date of birth, gender, payment details,
                      car details and driver’s licence details. Open assesses your application. If your application is
                      approved, Open is responsible for the administration of your ahm car insurance policy. Please
                      contact Open directly for any enquiries about your ahm car insurance policy cover, claims and
                      complaints.
                    </P>
                    <P>
                      For ahm home and contents insurance, Open collects your personal information in accordance with
                      its {LINK_URL_AHM_CAR_AND_HOME_INSURANCE_PRIVACY_POLICY}. Open will ask for your name, contact
                      details (address, email and phone number), ahm membership number, date of birth, gender, payment
                      details and details of your home and contents. Open assesses your application. If your application
                      is approved, Open is responsible for the administration of your ahm home & contents policy. Please
                      contact Open directly for any enquiries about your ahm home and contents policy cover, claims and
                      complaints.
                    </P>
                    <P>
                      Open shares your personal information with Medibank for marketing and customer service purposes.
                    </P>
                    <P>
                      Open also shares your personal information with Hollard for marketing and insurance purposes. View
                      more in {LINK_URL_PRIVACY_HOLLARD}.
                    </P>
                  </Col>
                </Row>
              </ContentSection>
              <ContentSection>
                <Row>
                  <Col md={12} df={8}>
                    <H3 looksLike="h4">Section 18: Travel insurance</H3>
                    <P>
                      Medibank Private Limited ABN 47 080 890 259 (MPL) is an Authorised Representative, AR 286089, of
                      Travel Insurance Partners Pty Limited ABN 73 144 049 230 AFSL 360138. MPL issues the insurance on
                      behalf of the insurer. The insurer is Zurich Australian Insurance Limited ABN 13 000 296 640
                      (Zurich). MPL collects your personal information including name, date of birth, payment details
                      and travel details. MPL assesses your application. If your application is approved, MPL provides
                      your personal information to Travel Insurance Partners, a subsidiary of Zurich which is
                      responsible for the administration of your travel insurance policy.
                    </P>
                  </Col>
                </Row>
              </ContentSection>
              <ContentSection>
                <Row>
                  <Col md={12} df={8}>
                    <H3 looksLike="h4">Section 19: Members of the MHS Group</H3>
                    <P>
                      For the purposes of the definition of “MHS Group” used in this Privacy Policy, the members of the
                      MHS Group are:
                    </P>
                    <List>
                      <li>Australian Health Management Group Pty Limited (ahm) (ABN 96 003 683 298),</li>
                      <li>
                        Medibank Health Solutions Pty Limited (ABN 99 078 943 791) (formerly Health Services Australia
                        Pty Ltd) and the following subsidiary companies -
                        <List>
                          <li>MHSI Pty Ltd (ABN 57 659 284 607),</li>
                          <li>MH Operations Pty Ltd (ABN 15 659 287 680),</li>
                          <li>
                            Integrated Care Services Pty Ltd (ABN 71 059 950 695), trading as Amplar Preventative
                            Health,
                          </li>
                          <li>
                            Medibank Health Solutions Telehealth Pty Limited (ABN 40 069 396 792), trading as Amplar
                            Virtual Health,
                          </li>
                          <li>MH Solutions Investments Pty Ltd (ABN 52 642 022 124),</li>
                          <li>Medi Financial Services Pty Ltd (ABN 94 138 752 815),</li>
                          <li>Live Better Management Pty Ltd (ABN 93 003 457 289), and</li>
                          <li>
                            MH Investment Holdings Pty Ltd (ABN 18 169 818 884) and the following subsidiaries:
                            <List>
                              <li>
                                Amplar Home Health Pty Ltd (ABN 59 008 193 100), formerly Home Support Services Pty Ltd
                                and Healthstrong Pty Ltd (ABN 61 155 277 919) trading as Amplar Allied Health.
                              </li>
                            </List>
                          </li>
                        </List>
                      </li>
                    </List>
                  </Col>
                </Row>
              </ContentSection>
              <ContentSection>
                <Row>
                  <Col md={12} df={8}>
                    <H3 looksLike="h4">Section 20: Medibank Group Companies</H3>
                    <P>For the purposes of this Privacy Policy, the ‘Medibank Group Companies‘ are:</P>
                    <List>
                      <li>Medibank (including Medibank trading as ahm),</li>
                      <li>MHS Group, and</li>
                      <li>Medibank Private Employee Share Plan Trust (ABN 53 501 924 436).</li>
                    </List>
                  </Col>
                </Row>
              </ContentSection>
            </LastPart>
          </Wrapper>
        </SectionContainer>
        <BackToTop />
      </ContentContainer>
    </LegacyBorderedWrapper>
  );
};
export default PrivacyPolicyPage;
